import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import anime from "animejs";
import { Settings } from "../../../settings";
import { formatToCustomLocale } from "../../../utilities";
import { PixiSettings } from "../../../pixiSettings";
import { SOUND_NAME } from "../../../sounds";
import { soundManager } from "../../../SoundManager";

// Constants
const RENDER_PIPELINE_ALPHA = 0.001;
const VISIBLE_ALPHA = 1;

// Device and orientation-specific configurations
const COLLECTOR_CONFIG = {
  MOBILE: {
    PORTRAIT: {
      spineScaleMultiplier: 1.1,
      textSizeMultiplier: 2.0,
      yOffsetMultiplier: 0.275, // Multiplied by symbolHeight
    },
    LANDSCAPE: {
      spineScaleMultiplier: 1.1,
      textSizeMultiplier: 2.0,
      yOffsetMultiplier: 0.275, // Multiplied by symbolHeight
    },
  },
  TABLET: {
    PORTRAIT: {
      spineScaleMultiplier: 1.1,
      textSizeMultiplier: 1.9,
      yOffsetMultiplier: 0.275, // Multiplied by symbolHeight
    },
    LANDSCAPE: {
      spineScaleMultiplier: 1.1,
      textSizeMultiplier: 1.3,
      yOffsetMultiplier: 0.275, // Multiplied by symbolHeight
    },
  },
  DESKTOP: {
    LANDSCAPE: {
      spineScaleMultiplier: 1.1,
      textSizeMultiplier: 1.15,
      yOffsetMultiplier: 0.275, // Multiplied by symbolHeight
    },
  },
};

/**
 * HoldAndWinCollectorDisplayController - Manages the collector spine animation
 * Shows collector display during Hold and Win collection phase
 * Plays orientation-specific animations (portrait vs landscape)
 */
class HoldAndWinCollectorDisplayController {
  constructor(container, dimensions, symbolHeight, device, orientation) {
    this.container = container;
    this.dimensions = dimensions;
    this.symbolHeight = symbolHeight;
    this.device = device;
    this.orientation = orientation;

    // Spine instance
    this.collectorSpine = null;
    this.initialized = false;
    this.isCleanedUp = false;
    this.isVisible = false;

    // Text container attached to spine bone
    this.collectorTextContainer = null;
    this.collectorText = null;
    this.currentValue = 0;
    this.boneUpdateTicker = null;

    // Counter animation
    this.counterAnimationRef = null;
    this.maxWinThreshold = 0;
    this.onMaxWinCallback = null;
    this.isCounterSoundPlaying = false;

    // Skip mode flag - when true, sound plays only once
    this.isSkipMode = false;
    this.hasPlayedSkipSound = false;

    // Internal caches for performance optimization
    this._configCache = null; // Cache for getConfig result
    this._deviceScaleMultiplierCache = null; // Cache for getDeviceScaleMultiplier result
    this._availableAnimationsCache = null; // Cache for getAvailableAnimations result
    this._scaleCache = null; // Cache for applyScaleAndCenter scale calculation
    this._fontSizeCache = null; // Cache for updateTextSize font size calculation
    this._lastSymbolHeight = null; // Track symbol height changes for cache invalidation
    this._lastDevice = null; // Track device changes for cache invalidation
    this._lastOrientation = null; // Track orientation changes for cache invalidation

    this.initializeCollectorSpine();
  }

  /**
   * Check if controller is ready for operations
   */
  isReady() {
    return this.collectorSpine?.state && this.initialized && !this.isCleanedUp;
  }

  /**
   * Get configuration based on current device and orientation
   */
  getConfig() {
    // Return cached result if available and device/orientation haven't changed
    if (
      this._configCache !== null &&
      this._lastDevice === this.device &&
      this._lastOrientation === this.orientation
    ) {
      return this._configCache;
    }

    const device = this.device || PixiSettings.DEVICE.DESKTOP;
    const orientation = this.orientation || PixiSettings.ORIENTATION.LANDSCAPE;

    // Map device string to config key
    let deviceKey;
    if (device === PixiSettings.DEVICE.MOBILE) {
      deviceKey = "MOBILE";
    } else if (device === PixiSettings.DEVICE.TABLET) {
      deviceKey = "TABLET";
    } else {
      deviceKey = "DESKTOP";
    }

    // Map orientation string to config key
    const orientationKey =
      orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? "PORTRAIT"
        : "LANDSCAPE";

    // Get device-specific config, fallback to DESKTOP LANDSCAPE if not found
    const deviceConfig =
      COLLECTOR_CONFIG[deviceKey] || COLLECTOR_CONFIG.DESKTOP;
    const config = deviceConfig[orientationKey] ||
      deviceConfig.LANDSCAPE ||
      COLLECTOR_CONFIG.DESKTOP.LANDSCAPE;

    // Cache result
    this._configCache = config;
    this._lastDevice = this.device;
    this._lastOrientation = this.orientation;

    return config;
  }

  /**
   * Apply proportional scaling and centering to spine animation
   */
  applyScaleAndCenter() {
    if (!this.collectorSpine || !this.dimensions) return;

    // Check cache (only if dimensions haven't changed)
    if (
      this._scaleCache !== null &&
      this._lastSymbolHeight === this.symbolHeight &&
      this._lastDevice === this.device &&
      this._lastOrientation === this.orientation
    ) {
      // Apply cached scale
      this.collectorSpine.scale.set(this._scaleCache.scale);
      this.collectorSpine.x = 0;
      this.collectorSpine.y = 0;
      return;
    }

    // Get orientation-specific config
    const config = this.getConfig();

    // Proportional scaling based on symbol size
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    // Apply device/orientation-specific multipliers
    const deviceMultiplier = this.getDeviceScaleMultiplier();
    scale *= deviceMultiplier;

    // Apply configuration multiplier for overall size
    scale *= config.spineScaleMultiplier;

    // Cache result
    this._scaleCache = { scale };
    this._lastSymbolHeight = this.symbolHeight;
    this._lastDevice = this.device;
    this._lastOrientation = this.orientation;

    this.collectorSpine.scale.set(scale);

    // Center the collector (parent container handles main positioning)
    this.collectorSpine.x = 0;
    this.collectorSpine.y = 0;
  }

  /**
   * Get device-specific scale multiplier for spine animations
   */
  getDeviceScaleMultiplier() {
    // Return cached result if available and device/orientation haven't changed
    if (
      this._deviceScaleMultiplierCache !== null &&
      this._lastDevice === this.device &&
      this._lastOrientation === this.orientation
    ) {
      return this._deviceScaleMultiplierCache;
    }

    const multiplier = 1.0;
    
    // Cache result
    this._deviceScaleMultiplierCache = multiplier;
    
    return multiplier;
  }

  /**
   * Update dimensions and symbol sizes, then recalculate scale and position
   */
  updateDimensions(dimensions, symbolHeight, device, orientation) {
    const oldDimensions = this.dimensions;
    const oldOrientation = this.orientation;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== dimensions?.width ||
      oldDimensions.height !== dimensions?.height ||
      this.device !== device ||
      this.orientation !== orientation;

    const symbolHeightChanged = this.symbolHeight !== symbolHeight;
    const orientationChanged = oldOrientation !== orientation;

    if (!dimensionsChanged && !symbolHeightChanged) {
      return;
    }

    this.dimensions = dimensions;
    this.symbolHeight = symbolHeight;
    this.device = device;
    this.orientation = orientation;

    // Invalidate caches when dimensions change
    if (dimensionsChanged || symbolHeightChanged || orientationChanged) {
      this._configCache = null;
      this._scaleCache = null;
      this._fontSizeCache = null;
      this._lastSymbolHeight = null;
      this._lastDevice = null;
      this._lastOrientation = null;
    }

    if (
      this.collectorSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightChanged)
    ) {
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
      }
    }

    // Recreate text with new font size if orientation or symbol height changed
    if (
      (orientationChanged || symbolHeightChanged) &&
      this.collectorText &&
      this.collectorTextContainer
    ) {
      this.updateTextSize();
    }
  }

  /**
   * Update text size based on current orientation and symbol height
   */
  updateTextSize() {
    if (!this.collectorText || !this.symbolHeight) return;

    // Check cache (only if dimensions haven't changed)
    if (
      this._fontSizeCache !== null &&
      this._lastSymbolHeight === this.symbolHeight &&
      this._lastDevice === this.device &&
      this._lastOrientation === this.orientation
    ) {
      this.collectorText.fontSize = this._fontSizeCache;
      return;
    }

    const config = this.getConfig();
    const baseFontSize = Settings.SYMBOL_HEIGHT * 0.25;
    const fontSize = Math.floor(
      baseFontSize *
        (this.symbolHeight / Settings.SYMBOL_HEIGHT) *
        config.textSizeMultiplier
    );

    // Cache result
    this._fontSizeCache = fontSize;
    this._lastSymbolHeight = this.symbolHeight;
    this._lastDevice = this.device;
    this._lastOrientation = this.orientation;

    this.collectorText.fontSize = fontSize;
  }

  /**
   * Initialize the collector spine
   */
  initializeCollectorSpine() {
    try {
      this.collectorSpine = this.createCollectorSpine();

      if (!this.collectorSpine) {
        return;
      }

      const missingComponents = [];
      if (!this.collectorSpine.skeleton) missingComponents.push("skeleton");
      if (!this.collectorSpine.state) missingComponents.push("state");
      if (!this.collectorSpine.skeleton?.data)
        missingComponents.push("skeleton.data");

      if (missingComponents.length > 0) {
        return;
      }

      this.container.addChild(this.collectorSpine);
      this.collectorSpine.zIndex = 20;

      this.applyScaleAndCenter();

      if (this.collectorSpine.parent !== this.container) {
        return;
      }

      // Initialize spine structure
      this.initializeSpineStructure();

      // Invalidate animation cache when spine is initialized (spine structure may have changed)
      this._availableAnimationsCache = null;

      this.initialized = true;
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Initialize skeleton by setting an animation to populate spine structure
   */
  initializeSpineStructure() {
    if (!this.collectorSpine || !this.collectorSpine.state) {
      return;
    }

    try {
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        // Use idle as default initialization
        const idleAnimation = animations.find((anim) => anim === "idle");
        const animationToUse = idleAnimation || animations[0];

        this.collectorSpine.state.setAnimation(0, animationToUse, false);
        this.collectorSpine.state.update(0.016);

        try {
          if (this.collectorSpine.skeleton.physics !== undefined) {
            this.collectorSpine.skeleton.updateWorldTransform();
          }
        } catch (transformError) {
          // Skip if physics not initialized
        }

        // Clear tracks after warmup
        this.collectorSpine.state.clearTracks();
      }

      // Keep spine ready but hidden initially (will be shown when collection starts)
      // Using 0.001 instead of 0 keeps it in render pipeline for better performance
      this.collectorSpine.alpha = RENDER_PIPELINE_ALPHA;
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Create collector spine from loaded assets
   */
  createCollectorSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "hns_collector-skeleton";
      const atlasAlias = "hns_collector-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const collectorSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (
        !collectorSpine ||
        !collectorSpine.skeleton ||
        !collectorSpine.state
      ) {
        return null;
      }

      if (
        !collectorSpine.skeleton.data ||
        !collectorSpine.skeleton.data.animations
      ) {
        return null;
      }

      return collectorSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get available animations from the spine
   */
  getAvailableAnimations() {
    // Return cached result if available
    if (this._availableAnimationsCache) {
      return this._availableAnimationsCache;
    }

    const animations = this.collectorSpine?.skeleton?.data?.animations.map((a) => a.name) || [];
    
    // Cache result
    this._availableAnimationsCache = animations;
    
    return animations;
  }

  /**
   * Check if animation exists
   */
  hasAnimation(animationName) {
    return this.collectorSpine?.skeleton?.data?.animations.some(
      (anim) => anim.name === animationName
    );
  }

  /**
   * Get animation name (no prefix needed)
   */
  getAnimationName(baseName) {
    return baseName;
  }

  /**
   * Initialize text container and attach to spine bone
   * Creates a container as a child of the spine that follows the "collector" bone
   */
  initializeCollectorText() {
    if (this.collectorTextContainer) return;

    if (!this.collectorSpine || !this.collectorSpine.skeleton) {
      return;
    }

    // Create container as child of spine (will follow bone transform)
    this.collectorTextContainer = new PIXI.Container();
    this.collectorTextContainer.name = "collector_text_attached_to_bone";
    this.collectorTextContainer.alpha = 0.001;
    this.collectorTextContainer.zIndex = 1000;

    this.collectorSpine.addChild(this.collectorTextContainer);
    this.collectorSpine.sortableChildren = true;

    // Create BitmapText inside the container
    const config = this.getConfig();
    const baseFontSize = Settings.SYMBOL_HEIGHT * 0.25;
    const fontSize = Math.floor(
      baseFontSize *
        (this.symbolHeight / Settings.SYMBOL_HEIGHT) *
        config.textSizeMultiplier
    );

    this.collectorText = new PIXI.BitmapText("0", {
      fontName: "font",
      fontSize: fontSize,
    });
    this.collectorText.anchor.set(0.5);
    this.collectorText.name = "collector_value_text";
    this.collectorText.x = 0;
    this.collectorText.y = 0;

    this.collectorTextContainer.addChild(this.collectorText);
  }

  /**
   * Update text container transform from spine's "collect_txt" bone
   * Called every frame while spine is animating
   */
  updateTextFromBone() {
    if (
      !this.collectorSpine ||
      !this.collectorTextContainer ||
      !this.collectorSpine.skeleton
    ) {
      return;
    }

    const collectTxtBone = this.collectorSpine.skeleton.findBone("collect_txt");
    if (!collectTxtBone) {
      return;
    }

    // Apply bone's world transform to text container
    this.collectorTextContainer.x = collectTxtBone.worldX;
    this.collectorTextContainer.y = collectTxtBone.worldY;

    // Keep text horizontal (don't inherit bone rotation)
    this.collectorTextContainer.rotation = 0;

    // Let text inherit bone scale naturally - it should follow all spine animations
    const boneScaleX = collectTxtBone.getWorldScaleX();
    const boneScaleY = collectTxtBone.getWorldScaleY();
    this.collectorTextContainer.scale.set(
      Math.abs(boneScaleX),
      Math.abs(boneScaleY)
    );
  }

  /**
   * Start syncing text with spine bone every frame
   * Uses PIXI.Ticker to update text transform from bone
   */
  startBoneUpdateLoop() {
    if (!this.collectorSpine || !this.collectorTextContainer) return;

    if (!this.boneUpdateTicker) {
      this.boneUpdateTicker = () => {
        // Defensive check: stop if cleaned up
        if (this.isCleanedUp || !this.collectorTextContainer || !this.isVisible)
          return;
        this.updateTextFromBone();
      };
      PIXI.Ticker.shared.add(this.boneUpdateTicker);
    }
  }

  /**
   * Stop syncing text with spine bone
   */
  stopBoneUpdateLoop() {
    if (this.boneUpdateTicker) {
      PIXI.Ticker.shared.remove(this.boneUpdateTicker);
      this.boneUpdateTicker = null;
    }
  }

  /**
   * Enable skip mode - sound will play only once for all updates
   */
  enableSkipMode() {
    this.isSkipMode = true;
    this.hasPlayedSkipSound = false;
  }

  /**
   * Disable skip mode - sound will play normally for each update
   */
  disableSkipMode() {
    this.isSkipMode = false;
    this.hasPlayedSkipSound = false;
  }

  /**
   * Update the displayed value and play hit animation (INSTANT - no animation)
   * This is kept for backward compatibility but should not be used during collection
   */
  updateCollectorValue(value) {
    if (!this.collectorTextContainer) {
      this.initializeCollectorText();
    }

    if (!this.collectorText) return;

    this.currentValue = value;
    const formattedValue = formatToCustomLocale(Math.floor(value));
    this.collectorText.text = formattedValue;

    // Show text container if hidden
    if (this.collectorTextContainer && this.collectorTextContainer.alpha < 1) {
      this.collectorTextContainer.alpha = 1;
    }

    // Play hit animation when value is added (sound will play inside playHitAnimation)
    this.playHitAnimation();
  }

  /**
   * Update collector display (internal method for animation)
   * Updates text without playing hit animation
   */
  updateCollectorDisplay(value) {
    if (!this.collectorTextContainer) {
      this.initializeCollectorText();
    }

    if (!this.collectorText) return;

    this.currentValue = value;
    const formattedValue = formatToCustomLocale(Math.floor(value));
    this.collectorText.text = formattedValue;

    // Show text container if hidden
    if (this.collectorTextContainer && this.collectorTextContainer.alpha < 1) {
      this.collectorTextContainer.alpha = 1;
    }
  }

  /**
   * Set max win threshold and callback for counter animation
   * @param {number} threshold - Max win threshold (bet * 5000)
   * @param {Function} callback - Callback to trigger when max win is reached during animation
   */
  setMaxWinCheck(threshold, callback) {
    this.maxWinThreshold = threshold;
    this.onMaxWinCallback = callback;
  }

  /**
   * Start counter animation from current value to target value
   * ALWAYS animates incrementally, never instant
   * @param {number} toValue - Target value to animate to
   * @param {number} duration - Animation duration in ms
   * @param {Function} onComplete - Callback when animation completes normally
   */
  startCounterAnimation(toValue, duration, onComplete) {
    // Initialize text container if needed
    if (!this.collectorTextContainer) {
      this.initializeCollectorText();
    }

    // Stop any existing animation
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    // Play collector counter sound (looping) - only if not already playing
    if (!this.isCounterSoundPlaying) {
      soundManager.play(SOUND_NAME.collector_counter);
      this.isCounterSoundPlaying = true;
    }

    // Play hit animation at the start
    this.playHitAnimation();

    this.counterAnimationRef = anime({
      targets: this,
      currentValue: toValue,
      duration: duration,
      easing: "easeOutQuad",
      update: () => {
        this.updateCollectorDisplay(this.currentValue);

        // CHECK FOR MAX WIN DURING ANIMATION
        if (this.maxWinThreshold > 0 && this.currentValue >= this.maxWinThreshold) {
          // MAX WIN REACHED! Stop animation immediately
          this.counterAnimationRef.pause();
          this.counterAnimationRef = null;
          soundManager.stop(SOUND_NAME.collector_counter);
          this.isCounterSoundPlaying = false;

          // Set to exact max win value
          this.currentValue = this.maxWinThreshold;
          this.updateCollectorDisplay(this.maxWinThreshold);

          // Trigger max win callback
          if (this.onMaxWinCallback) {
            this.onMaxWinCallback(this.maxWinThreshold);
          }
          return; // Stop further execution
        }
      },
      complete: () => {
        this.currentValue = toValue;
        this.updateCollectorDisplay(toValue);
        this.counterAnimationRef = null;
        // Don't stop sound here - let it play continuously until collection ends

        if (onComplete) onComplete();
      },
    });
  }

  /**
   * Check if counter is currently animating
   * @returns {boolean} True if counter animation is active
   */
  isCounterAnimating() {
    return this.counterAnimationRef !== null;
  }

  /**
   * Stop counter animation immediately
   * Also stops the sound automatically
   */
  stopCounterAnimation() {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }
    // Always stop sound when animation stops (even if animation wasn't running)
    if (this.isCounterSoundPlaying) {
      soundManager.stop(SOUND_NAME.collector_counter);
      this.isCounterSoundPlaying = false;
    }
  }

  /**
   * Play hit animation on track 1 (doesn't interrupt idle on track 0)
   * Only plays sound when animation actually plays (collector is visible)
   * In skip mode, sound plays only once for all updates
   */
  playHitAnimation() {
    if (!this.isReady() || !this.isVisible) {
      return;
    }

    const hitAnimation = "hit";

    if (this.hasAnimation(hitAnimation)) {
      try {
        // Play sound logic:
        // - Normal mode: play sound every time
        // - Skip mode: play sound only once (first update)
        if (this.isSkipMode) {
          if (!this.hasPlayedSkipSound) {
            soundManager.play(SOUND_NAME.collector_add);
            this.hasPlayedSkipSound = true;
          }
        } else {
          // Normal mode - play sound every time
          soundManager.play(SOUND_NAME.collector_add);
        }

        // Clear track 1 first to prevent listener accumulation
        this.collectorSpine.state.clearTrack(1);
        // Play hit on track 1 so it doesn't interrupt idle on track 0
        this.collectorSpine.state.setAnimation(1, hitAnimation, false);
      } catch (error) {
        // Silent fail
      }
    }
  }

  /**
   * Show collector with intro animation
   */
  showCollector() {
    if (!this.isReady()) {
      return;
    }

    // Prevent double-showing if already visible
    if (this.isVisible) {
      return;
    }

    // Initialize text if not already done
    if (!this.collectorTextContainer) {
      this.initializeCollectorText();
    }

    // Reset value and skip mode
    this.currentValue = 0;
    this.isSkipMode = false;
    this.hasPlayedSkipSound = false;

    if (this.collectorText) {
      this.collectorText.text = "0";
    }
    if (this.collectorTextContainer) {
      this.collectorTextContainer.alpha = 0.001; // Will show when first value is added
    }

    const inAnimation = this.getAnimationName("in");

    if (this.hasAnimation(inAnimation)) {
      try {
        // Play collector appear sound
        soundManager.play(SOUND_NAME.collector_appear);

        // Clear tracks and listeners first
        this.collectorSpine.state.clearTracks();
        this.collectorSpine.state.clearListeners();

        // Set animation WHILE INVISIBLE to prepare it
        this.collectorSpine.state.setAnimation(0, inAnimation, false);

        // Transition to idle after intro completes
        const idleAnimation = this.getAnimationName("idle");
        if (this.hasAnimation(idleAnimation)) {
          this.collectorSpine.state.addAnimation(0, idleAnimation, true, 0);
        }

        // Use requestAnimationFrame to ensure spine updates before becoming visible
        // This prevents any flash or visual artifacts
        requestAnimationFrame(() => {
          if (!this.collectorSpine || this.isCleanedUp) return;

          // NOW make visible after spine has had a chance to update
          this.isVisible = true;
          this.collectorSpine.alpha = VISIBLE_ALPHA;

          // Start bone update loop to sync text with spine animation
          this.startBoneUpdateLoop();
        });
      } catch (error) {
        // Fallback to idle
        this.playIdle();
      }
    } else {
      // Fallback to idle if intro doesn't exist
      this.isVisible = true;
      this.collectorSpine.alpha = VISIBLE_ALPHA;
      this.startBoneUpdateLoop();
      this.playIdle();
    }
  }

  /**
   * Play idle animation (looping)
   */
  playIdle() {
    if (!this.isReady()) {
      return;
    }

    const idleAnimation = this.getAnimationName("idle");

    if (this.hasAnimation(idleAnimation)) {
      try {
        this.collectorSpine.state.clearTracks();
        this.collectorSpine.state.clearListeners();
        this.collectorSpine.state.setAnimation(0, idleAnimation, true);
      } catch (error) {
        // Silent fail
      }
    }
  }

  /**
   * Hide collector with outro animation
   */
  hideCollector() {
    if (!this.isReady() || !this.isVisible) {
      return;
    }

    // If counter animation is active, complete it to final value before stopping
    if (this.counterAnimationRef) {
      // Get the target value from the animation
      const targetValue = this.counterAnimationRef.animations[0].toValue;
      if (targetValue !== undefined) {
        this.currentValue = targetValue;
        this.updateCollectorDisplay(targetValue);
      }
    }

    // Stop counter animation if active
    this.stopCounterAnimation();

    const outAnimation = this.getAnimationName("out");

    if (this.hasAnimation(outAnimation)) {
      try {
        // Play collector appear sound (same for out animation)
        soundManager.play(SOUND_NAME.collector_appear);

        this.collectorSpine.state.clearTracks();
        this.collectorSpine.state.clearListeners();

        // IMPORTANT: Reset skeleton to clear the collector bone translation from idle/hit
        // The out animation uses root bone translation instead, so we need a clean slate
        // Without this, we get double offset (collector bone + root bone translations)
        if (this.collectorSpine.skeleton) {
          this.collectorSpine.skeleton.setToSetupPose();
        }

        // Play outro animation
        this.collectorSpine.state.setAnimation(0, outAnimation, false);

        // Hide after outro completes
        this.collectorSpine.state.addListener({
          complete: (entry) => {
            if (entry.animation.name === outAnimation) {
              this.collectorSpine.alpha = RENDER_PIPELINE_ALPHA;
              this.isVisible = false;

              // Hide text container
              if (this.collectorTextContainer) {
                this.collectorTextContainer.alpha = 0.001;
              }

              // Stop bone update loop
              this.stopBoneUpdateLoop();

              // Reset skeleton to setup pose to clear any bone transforms from animations
              if (this.collectorSpine.skeleton) {
                this.collectorSpine.skeleton.setToSetupPose();
              }
            }
          },
        });
      } catch (error) {
        // Fallback: hide immediately
        this.collectorSpine.alpha = RENDER_PIPELINE_ALPHA;
        this.isVisible = false;
        if (this.collectorTextContainer) {
          this.collectorTextContainer.alpha = 0.001;
        }
        this.stopBoneUpdateLoop();
        // Reset skeleton
        if (this.collectorSpine.skeleton) {
          this.collectorSpine.skeleton.setToSetupPose();
        }
      }
    } else {
      // Fallback: hide immediately
      this.collectorSpine.alpha = RENDER_PIPELINE_ALPHA;
      this.isVisible = false;
      if (this.collectorTextContainer) {
        this.collectorTextContainer.alpha = 0.001;
      }
      this.stopBoneUpdateLoop();
      // Reset skeleton
      if (this.collectorSpine.skeleton) {
        this.collectorSpine.skeleton.setToSetupPose();
      }
    }
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    if (this.isCleanedUp) {
      return;
    }
    this.isCleanedUp = true;

    // Stop counter animation
    this.stopCounterAnimation();

    // Stop bone update loop
    this.stopBoneUpdateLoop();

    // Cleanup text container
    if (this.collectorTextContainer) {
      try {
        if (this.collectorText) {
          this.collectorText.destroy();
          this.collectorText = null;
        }
        if (this.collectorTextContainer.parent) {
          this.collectorTextContainer.parent.removeChild(
            this.collectorTextContainer
          );
        }
        this.collectorTextContainer.destroy({ children: true });
      } catch (error) {
        // Silent fail
      }
      this.collectorTextContainer = null;
    }

    if (this.collectorSpine) {
      this.collectorSpine.state?.clearListeners();
      this.collectorSpine.state?.clearTracks();
      if (this.collectorSpine.state) this.collectorSpine.state.timeScale = 1.0;

      if (this.container?.children.includes(this.collectorSpine)) {
        this.container.removeChild(this.collectorSpine);
      }
      this.collectorSpine.destroy();
      this.collectorSpine = null;
    }

    // Clear caches
    this._configCache = null;
    this._deviceScaleMultiplierCache = null;
    this._availableAnimationsCache = null;
    this._scaleCache = null;
    this._fontSizeCache = null;
    this._lastSymbolHeight = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    this.initialized = false;
    this.isVisible = false;
  }

  /**
   * Static method to get config for a specific device and orientation
   * Used by the JSX component to calculate Y offset
   */
  static getConfigForDevice(device, orientation) {
    device = device || PixiSettings.DEVICE.DESKTOP;
    orientation = orientation || PixiSettings.ORIENTATION.LANDSCAPE;

    // Map device string to config key
    let deviceKey;
    if (device === PixiSettings.DEVICE.MOBILE) {
      deviceKey = "MOBILE";
    } else if (device === PixiSettings.DEVICE.TABLET) {
      deviceKey = "TABLET";
    } else {
      deviceKey = "DESKTOP";
    }

    // Map orientation string to config key
    const orientationKey =
      orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? "PORTRAIT"
        : "LANDSCAPE";

    // Get device-specific config, fallback to DESKTOP LANDSCAPE if not found
    const deviceConfig =
      COLLECTOR_CONFIG[deviceKey] || COLLECTOR_CONFIG.DESKTOP;
    return (
      deviceConfig[orientationKey] ||
      deviceConfig.LANDSCAPE ||
      COLLECTOR_CONFIG.DESKTOP.LANDSCAPE
    );
  }
}

export default HoldAndWinCollectorDisplayController;
